import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Save, ChevronRight, ArrowLeft, Search, User as UserIcon, MapPin, Landmark, Percent, FileText, Paperclip, Briefcase } from 'lucide-react'
import { PageHeader } from '@/components/ui/PageHeader'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Badge } from '@/components/ui/Badge'
import { StatusToggle } from '@/components/ui/StatusToggle'
import { PercentInput } from '@/components/ui/PercentInput'
import { FormSidemenu, type FormSection } from '@/components/ui/FormSidemenu'
import { maskPhoneInput, onlyDigits } from '@/lib/format'
import { normalizeForMatch } from '@/lib/validators'
import { toast } from '@/components/ui/Toast'
import { UsersService, type UserSimpleOption } from '@/services/modules/UsersService'
import { HierarchiesService, type Hierarchy } from '@/services/modules/HierarchiesService'
import { CredentialingCommissionService, type CredentialingCommissionStructure } from '@/services/modules/CredentialingService'
import { CommissionStructuresService, type CommissionStructure } from '@/services/modules/CommissionStructuresService'
import { LookupService, type State, type City } from '@/services/modules/LookupService'
import { BanksService, type Bank } from '@/services/modules/BanksService'
import { SalesLinkService } from '@/services/modules/SalesLinkService'
import { DocumentsManager } from '@/components/DocumentsManager'
import { UserVacations } from './UserVacations'
import { standardDocuments } from '@/services/modules/DocumentsService'
import { useAuthority } from '@/hooks/useAuthority'
import { USER_TYPE, USER_TYPE_META, userTypeLabel } from './userTypes'

const WIZARD_TYPES = [
  USER_TYPE.ADMINISTRATOR, USER_TYPE.COLLABORATOR,
  USER_TYPE.DIRECTOR, USER_TYPE.SUPERVISOR, USER_TYPE.AFFILIATE,
]
const COMMERCIAL_TYPES: string[] = [USER_TYPE.DIRECTOR, USER_TYPE.SUPERVISOR, USER_TYPE.AFFILIATE]

// Iguais ao /credentialing/providers/novo
const TRANSACTION_TYPES = [
  { value: 'TRANSFERÊNCIA', label: 'Transferência' },
  { value: 'PIX',          label: 'PIX' },
  { value: 'DEPÓSITO',     label: 'Depósito' },
]
const ACCOUNT_TYPES = [
  { value: 'CORRENTE',  label: 'Conta corrente' },
  { value: 'POUPANÇA',  label: 'Conta poupança' },
  { value: 'SALÁRIO',   label: 'Conta salário' },
  { value: 'CONJUNTA',  label: 'Conta conjunta' },
]
const PERSON_OPTS = [
  { value: 'PESSOA JURÍDICA', label: 'Pessoa Jurídica' },
  { value: 'PESSOA FÍSICA',   label: 'Pessoa Física' },
]
const maskCep = (v: string) => onlyDigits(v).slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2')
const maskCpf = (v: string) => onlyDigits(v).slice(0, 11)
  .replace(/^(\d{3})(\d)/, '$1.$2').replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1-$2')
const maskCnpj = (v: string) => onlyDigits(v).slice(0, 14)
  .replace(/^(\d{2})(\d)/, '$1.$2').replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/\.(\d{3})(\d)/, '.$1/$2').replace(/(\d{4})(\d)/, '$1-$2')
const maskDoc = (t: string, v: string) => t === 'PESSOA FÍSICA' ? maskCpf(v) : maskCnpj(v)

type CommissionMode = 'NONE' | 'NETWORK' | 'REGRESSIVE'

const EMPTY = {
  name: '', username: '', email: '', password: '', whatsapp: '', slug: '', managerId: '', hierarchyId: '',
  status: 'ATIVO' as 'ATIVO' | 'INATIVO', credentialingCommissionId: '', admissionDate: '',
  postalCode: '', address: '', number: '', complement: '', district: '', stateId: '', cityId: '',
  bankId: '', beneficiary: '', bankDocument: '', agency: '', account: '', pixKey: '',
  transaction: '', accountHolderType: '', accountType: '',
  commissionMode: 'NONE' as CommissionMode, networkPct: '', commissionId: '',
  admName: '', admEmail: '', billName: '', billEmail: '',
}

export function UserFormPage() {
  const nav = useNavigate()
  const { id } = useParams<{ id: string }>()
  const editing = !!id
  const { can } = useAuthority()
  const canEdit = can('administractive.users.edit')
  const canSave = editing ? canEdit : can('administractive.users.create')

  const [type, setType] = useState<string | null>(null)
  const [step, setStep] = useState<1 | 2>(editing ? 2 : 1)
  const [f, setF] = useState({ ...EMPTY })
  const patch = (p: Partial<typeof EMPTY>) => setF(prev => ({ ...prev, ...p }))

  const [errors, setErrors] = useState<Record<string, string>>({})
  const clearError = (k: string) => setErrors(prev => (prev[k] ? { ...prev, [k]: '' } : prev))

  const [hierarchies, setHierarchies] = useState<Hierarchy[]>([])
  const [credCommissions, setCredCommissions] = useState<CredentialingCommissionStructure[]>([])
  const [commercialCommissions, setCommercialCommissions] = useState<CommissionStructure[]>([])
  const [banks, setBanks] = useState<Bank[]>([])
  const [states, setStates] = useState<State[]>([])
  const [cities, setCities] = useState<City[]>([])
  const [managers, setManagers] = useState<UserSimpleOption[]>([])

  const [loading, setLoading] = useState(editing)
  const [saving, setSaving] = useState(false)
  const [cepLoading, setCepLoading] = useState(false)
  const [activeSection, setActiveSection] = useState('dados')

  const isCollaborator = type === USER_TYPE.COLLABORATOR
  const isAdmin = type === USER_TYPE.ADMINISTRATOR
  const isCommercial = type ? COMMERCIAL_TYPES.includes(type) : false
  const isAffiliate = type === USER_TYPE.AFFILIATE
  const needsManager = type === USER_TYPE.SUPERVISOR || type === USER_TYPE.AFFILIATE
  const meta = type ? USER_TYPE_META[type] : null

  useEffect(() => {
    HierarchiesService.list({ size: 0, status: 'ATIVO' }).then(r => setHierarchies(r.data)).catch(() => {})
    CredentialingCommissionService.list({ size: 0, status: 'ATIVO' }).then(r => setCredCommissions(r.data)).catch(() => {})
    CommissionStructuresService.list({ size: 0, status: 'ATIVO' }).then(r => setCommercialCommissions(r.data)).catch(() => {})
    BanksService.list({ size: 0 }).then(r => setBanks(r.data)).catch(() => {})
    LookupService.states().then(setStates).catch(() => {})
  }, [])

  useEffect(() => {
    // Supervisor → gestor é apenas Diretor; Afiliado → Diretor ou Supervisor.
    if (type === USER_TYPE.SUPERVISOR) UsersService.byType([USER_TYPE.DIRECTOR]).then(setManagers).catch(() => setManagers([]))
    else if (type === USER_TYPE.AFFILIATE) UsersService.byType([USER_TYPE.DIRECTOR, USER_TYPE.SUPERVISOR]).then(setManagers).catch(() => setManagers([]))
    else setManagers([])
  }, [type])

  useEffect(() => {
    if (!f.stateId) { setCities([]); return }
    LookupService.cities(f.stateId).then(setCities).catch(() => setCities([]))
  }, [f.stateId])

  useEffect(() => {
    if (!editing) return
    setLoading(true)
    UsersService.show(id!).then(u => {
      setType(u.type)
      const mode: CommissionMode = u.commissionId ? 'REGRESSIVE' : (u.networkCommissionPercentage != null ? 'NETWORK' : 'NONE')
      setF({
        ...EMPTY,
        name: u.name ?? '', username: u.username ?? '', email: u.email ?? '',
        whatsapp: u.whatsapp ? maskPhoneInput(u.whatsapp) : '',
        slug: u.slug ?? '', managerId: u.manager?.id ?? u.managerId ?? '',
        hierarchyId: u.hierarchy?.id ?? '', status: u.status ?? 'ATIVO',
        credentialingCommissionId: u.credentialingCommissionId ?? '',
        admissionDate: u.admissionDate ?? '',
        postalCode: u.address?.postalCode ? maskCep(u.address.postalCode) : '',
        address: u.address?.address ?? '', number: u.address?.number ?? '',
        complement: u.address?.complement ?? '', district: u.address?.district ?? '',
        stateId: u.address?.stateId ?? '', cityId: u.address?.cityId ?? '',
        bankId: u.bankDetails?.bankId ?? '', beneficiary: u.bankDetails?.beneficiary ?? '',
        bankDocument: u.bankDetails?.document ? maskDoc(u.bankDetails?.type ?? 'PESSOA JURÍDICA', u.bankDetails.document) : '',
        agency: u.bankDetails?.agency ?? '',
        account: u.bankDetails?.account ?? '', pixKey: u.bankDetails?.pixKey ?? '',
        transaction: u.bankDetails?.transaction ?? '', accountHolderType: u.bankDetails?.type ?? '',
        accountType: u.bankDetails?.accountType ?? '',
        commissionMode: mode,
        networkPct: u.networkCommissionPercentage != null ? String(Math.round(u.networkCommissionPercentage * 100)) : '',
        commissionId: u.commissionId ?? '',
        admName: u.administrativeManagerName ?? '', admEmail: u.administrativeManagerEmail ?? '',
        billName: u.billingManagerName ?? '', billEmail: u.billingManagerEmail ?? '',
      })
    }).catch(() => toast.error('Erro ao carregar o usuário.')).finally(() => setLoading(false))
  }, [id, editing])

  async function lookupCep() {
    const cep = onlyDigits(f.postalCode)
    if (cep.length !== 8) { toast.error({ title: 'CEP inválido', description: 'O CEP precisa ter 8 dígitos.' }); return }
    setCepLoading(true)
    try {
      const r = await SalesLinkService.lookupCep(cep)
      if (!r) { toast.error({ title: 'CEP não encontrado', description: 'Verifique o CEP digitado.' }); return }
      const st = states.find(s => s.abbreviation === r.stateAbbr)
      let cityId = ''
      if (st) {
        let cs = await LookupService.cities(st.id)
        let ci = cs.find(c => normalizeForMatch(c.city) === normalizeForMatch(r.cityName))
        if (!ci) {
          try { ci = await LookupService.findOrCreateCity(r.cityName, st.abbreviation); cs = [...cs, ci] } catch { /* ignore */ }
        }
        setCities(cs)
        cityId = ci?.id ?? ''
      }
      patch({ address: r.address ?? '', district: r.district ?? '', stateId: st?.id ?? '', cityId })
    } catch { toast.error('Não foi possível buscar o CEP.') }
    finally { setCepLoading(false) }
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {}
    if (!f.name.trim()) e.name = 'Informe o nome completo.'
    if (!f.username.trim()) e.username = 'Informe o usuário.'
    if (!editing && !f.password.trim()) e.password = 'Defina uma senha (mínimo de 8 caracteres).'
    if (f.password.trim() && f.password.trim().length < 8) e.password = 'A senha deve ter no mínimo 8 caracteres.'
    if (isCollaborator && !f.hierarchyId) e.hierarchyId = 'Selecione a hierarquia do colaborador.'
    if (needsManager && !f.managerId) e.managerId = 'Selecione o gestor.'
    const digits = onlyDigits(f.whatsapp)
    if (digits && ![10, 11].includes(digits.length)) e.whatsapp = 'O WhatsApp deve ter 10 ou 11 dígitos.'
    if (isCommercial && f.commissionMode === 'NETWORK') {
      const n = Number(f.networkPct)
      if (f.networkPct === '' || Number.isNaN(n) || n < 0 || n > 100) e.networkPct = 'Informe um percentual de 0 a 100.'
    }
    if (isCommercial && f.commissionMode === 'REGRESSIVE' && !f.commissionId) e.commissionId = 'Selecione a estrutura de comissão.'
    return e
  }

  async function submit() {
    if (!type) return
    const e = validate()
    setErrors(e)
    if (Object.keys(e).length > 0) {
      if (e.name || e.username || e.password || e.whatsapp || e.managerId || e.hierarchyId) setActiveSection('dados')
      else if (e.networkPct || e.commissionId) setActiveSection('comissoes')
      toast.error({ title: 'Verifique os campos', description: 'Há campos obrigatórios ou inválidos no formulário.' })
      return
    }
    if (isAffiliate && f.slug.trim()) {
      try {
        if (await UsersService.slugExists(f.slug.trim())) {
          setErrors(prev => ({ ...prev, slug: 'Este slug já está em uso.' }))
          setActiveSection('dados')
          toast.error({ title: 'Slug em uso', description: 'Escolha outro slug.' }); return
        }
      } catch { /* segue */ }
    }

    setSaving(true)
    try {
      const payload: Record<string, unknown> = {
        type, name: f.name.trim(), username: f.username.trim(),
        email: f.email.trim() || null,
        whatsapp: f.whatsapp.trim() ? onlyDigits(f.whatsapp) : null,
        status: f.status,
      }
      if (f.password.trim()) payload.password = f.password
      if (isCollaborator) payload.hierarchy_id = f.hierarchyId
      if (needsManager) payload.manager_id = f.managerId
      if (isAffiliate) payload.slug = f.slug.trim() || null
      if (isAdmin || isCollaborator) {
        payload.credentialing_commission_id = f.credentialingCommissionId || null
        payload.admission_date = f.admissionDate || null
      }

      if (isCommercial) {
        if (f.postalCode || f.address || f.cityId) {
          payload.address = {
            postal_code: onlyDigits(f.postalCode) || null, address: f.address || null,
            number: f.number || null, complement: f.complement || null,
            district: f.district || null, city_id: f.cityId || null,
          }
        }
        if (f.bankId || f.pixKey || f.account) {
          payload.bank = {
            bank_id: f.bankId || null, beneficiary: f.beneficiary || null, document: f.bankDocument ? onlyDigits(f.bankDocument) : null,
            agency: f.agency || null, account: f.account || null, pix_key: f.pixKey || null,
            transaction: f.transaction || null, type: f.accountHolderType || null, account_type: f.accountType || null,
          }
        }
        payload.network_commission_percentage = f.commissionMode === 'NETWORK' ? Number(f.networkPct) / 100 : null
        payload.commission_id = f.commissionMode === 'REGRESSIVE' ? f.commissionId : null
        if (isAffiliate) {
          payload.administrative_manager_name = f.admName || null
          payload.administrative_manager_email = f.admEmail || null
          payload.billing_manager_name = f.billName || null
          payload.billing_manager_email = f.billEmail || null
        }
      }

      if (editing) { await UsersService.update(id!, payload); toast.success({ title: 'Usuário atualizado', description: f.name }) }
      else { await UsersService.create(payload); toast.success({ title: 'Usuário criado', description: f.name }) }
      nav('/administractive/users')
    } catch (err) {
      toast.error((err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Erro ao salvar.')
    } finally { setSaving(false) }
  }

  // -------------------------------------------------------------- Step 1
  // Chooser SÓ no cadastro. No editar nunca mostra a tela de seleção de tipo
  // (evita o "flash" enquanto o tipo carrega — brecha de segurança).
  if (!editing && (step === 1 || !type)) {
    return (
      <>
        <PageHeader title="Novo usuário" subtitle="Selecione o tipo de usuário para começar"
          backTo="/administractive/users"
          crumbs={[{ label: 'Administração' }, { label: 'Usuários' }, { label: 'Novo' }]}
        />
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-6 sm:p-8">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Qual o tipo de usuário?</h2>
          <p className="text-xs text-gray-500 mb-5">Os campos do cadastro mudam conforme o tipo selecionado.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {WIZARD_TYPES.map(t => {
              const m = USER_TYPE_META[t]
              return (
                <button key={t} type="button" onClick={() => { setType(t); setStep(2) }}
                  className="cursor-pointer text-left rounded-md ring-1 ring-gray-200 p-5 transition flex items-start gap-3 hover:ring-brand-300 hover:bg-brand-50/30">
                  <span className="grid place-items-center h-10 w-10 rounded-md bg-brand-50 text-brand-600 shrink-0"><m.icon size={20} /></span>
                  <span className="min-w-0">
                    <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">{m.label} <ChevronRight size={14} className="text-gray-300" /></span>
                    <span className="block text-xs text-gray-500 mt-0.5">{m.description}</span>
                  </span>
                </button>
              )
            })}
          </div>
        </div>
      </>
    )
  }

  const header = (
    <PageHeader
      title={editing ? 'Editar usuário' : `Novo ${meta?.label.toLowerCase()}`}
      subtitle={meta?.description}
      backTo="/administractive/users"
      crumbs={[{ label: 'Administração' }, { label: 'Usuários' }, { label: editing ? 'Editar' : 'Novo' }]}
      actions={<Button onClick={submit} loading={saving} iconLeft={<Save size={14} />} disabled={loading || !canSave}
        title={canSave ? undefined : `Sem permissão para ${editing ? 'editar' : 'criar'} usuários`}>Salvar</Button>}
    />
  )

  // -------------------------------------------------------------- Admin / Colaborador (Dados | RH)
  if (!isCommercial) {
    const adminDadosErr = !!(errors.name || errors.username || errors.password || errors.whatsapp || errors.hierarchyId)
    const adminSections: FormSection[] = [
      {
        key: 'dados', label: 'Dados', icon: UserIcon, hasError: adminDadosErr,
        content: (
          <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-4">Dados de acesso</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2"><Input label="Nome completo" required value={f.name} onChange={e => { patch({ name: e.target.value }); clearError('name') }} placeholder="Ex.: João da Silva" error={errors.name} /></div>
              {isCollaborator && (
                <Select label="Hierarquia" required value={f.hierarchyId} onChange={e => { patch({ hierarchyId: e.target.value }); clearError('hierarchyId') }} placeholder="Selecione a hierarquia" options={hierarchies.map(h => ({ value: h.id, label: h.name }))} hint="Define exatamente o que este colaborador pode acessar." error={errors.hierarchyId} />
              )}
              <Input label="Usuário" required value={f.username} onChange={e => { patch({ username: e.target.value }); clearError('username') }} placeholder="Ex.: joao.silva" autoComplete="off" error={errors.username} />
              <Input label="E-mail" type="email" value={f.email} onChange={e => patch({ email: e.target.value })} placeholder="nome@empresa.com.br" autoComplete="off" />
              <Input label={editing ? 'Nova senha' : 'Senha'} required={!editing} type="password" value={f.password} onChange={e => { patch({ password: e.target.value }); clearError('password') }} placeholder={editing ? 'Deixe em branco para manter' : 'Mínimo de 8 caracteres'} autoComplete="new-password" error={errors.password} />
              <Input label="WhatsApp" value={f.whatsapp} onChange={e => { patch({ whatsapp: maskPhoneInput(e.target.value) }); clearError('whatsapp') }} placeholder="(11) 99999-9999" error={errors.whatsapp} />
              <Select label="Comissão de credenciamento" value={f.credentialingCommissionId} onChange={e => patch({ credentialingCommissionId: e.target.value })} placeholder="Sem comissão" options={credCommissions.map(c => ({ value: c.id, label: c.name }))} hint="Estrutura usada ao aprovar credenciados cadastrados por este usuário." />
              <div className="md:col-span-2 border-t border-gray-100 pt-4"><StatusToggle value={f.status} onChange={v => patch({ status: v })} /></div>
            </div>
          </div>
        ),
      },
      {
        key: 'rh', label: 'RH', icon: Briefcase,
        content: (
          <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-1">RH</h2>
            <p className="text-xs text-gray-500 mb-4">Informações de recursos humanos.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
              <Input label="Data de admissão" type="date" value={f.admissionDate} onChange={e => patch({ admissionDate: e.target.value })} clearable={false} />
            </div>
            <div className="mt-6 border-t border-gray-100 pt-5">
              <UserVacations userId={editing ? id! : null} canEdit={canEdit} />
            </div>
          </div>
        ),
      },
      {
        key: 'documentos', label: 'Documentos', icon: Paperclip,
        content: (
          <DocumentsManager
            service={standardDocuments(`/administractive/users/${id}`)}
            enabled={editing}
            readOnly={!canEdit}
            description="Anexe e gerencie os documentos do usuário (contrato, RG, comprovantes, atestados…)."
          />
        ),
      },
    ]
    return (
      <>
        {header}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {meta && <Badge variant={isAdmin ? 'violet' : 'blue'}>{meta.label}</Badge>}
            {isAdmin && <span className="text-xs text-gray-500">Acesso total ao sistema (automático).</span>}
          </div>
          {!editing && <Button size="xs" variant="ghost" iconLeft={<ArrowLeft size={12} />} onClick={() => setStep(1)}>Trocar tipo</Button>}
        </div>
        {loading ? <div className="py-10 text-center text-sm text-gray-400">Carregando</div>
          : <FormSidemenu sections={adminSections} activeKey={activeSection} onChange={setActiveSection} />}
      </>
    )
  }

  // -------------------------------------------------------------- Diretor / Supervisor / Afiliado (sidemenu)
  const dadosErr = !!(errors.name || errors.username || errors.password || errors.whatsapp || errors.managerId || errors.slug)
  const comissaoErr = !!(errors.networkPct || errors.commissionId)

  const sections: FormSection[] = [
    {
      key: 'dados', label: 'Dados', icon: UserIcon, hasError: dadosErr,
      content: (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Dados de acesso</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2"><Input label="Nome completo" required value={f.name} onChange={e => { patch({ name: e.target.value }); clearError('name') }} placeholder="Ex.: João da Silva" error={errors.name} /></div>
            <Input label="Usuário" required value={f.username} onChange={e => { patch({ username: e.target.value }); clearError('username') }} placeholder="Ex.: joao.silva" autoComplete="off" error={errors.username} />
            <Input label="E-mail" type="email" value={f.email} onChange={e => patch({ email: e.target.value })} placeholder="nome@empresa.com.br" autoComplete="off" />
            <Input label={editing ? 'Nova senha' : 'Senha'} required={!editing} type="password" value={f.password} onChange={e => { patch({ password: e.target.value }); clearError('password') }} placeholder={editing ? 'Deixe em branco para manter' : 'Mínimo de 8 caracteres'} autoComplete="new-password" error={errors.password} />
            <Input label="WhatsApp" value={f.whatsapp} onChange={e => { patch({ whatsapp: maskPhoneInput(e.target.value) }); clearError('whatsapp') }} placeholder="(11) 99999-9999" error={errors.whatsapp} />
            {needsManager && (
              <Select label="Gestor" required value={f.managerId} onChange={e => { patch({ managerId: e.target.value }); clearError('managerId') }}
                placeholder={isAffiliate ? 'Selecione o diretor ou supervisor' : 'Selecione o diretor'}
                options={managers.map(m => ({ value: m.id, label: isAffiliate ? `${m.name} (${userTypeLabel(m.type)})` : m.name }))} error={errors.managerId} />
            )}
            {isAffiliate && (
              <Input label="Slug (link público)" value={f.slug} onChange={e => { patch({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }); clearError('slug') }} placeholder="ex.: joao-silva" hint="Único no sistema. Deixe vazio para gerar automaticamente." error={errors.slug} />
            )}
            <div className="md:col-span-2 border-t border-gray-100 pt-4"><StatusToggle value={f.status} onChange={v => patch({ status: v })} /></div>
          </div>
        </div>
      ),
    },
    {
      key: 'localizacao', label: 'Localização', icon: MapPin,
      content: (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Localização</h2>
          <p className="text-xs text-gray-500 mb-4">Digite o CEP e clique em buscar para preencher automaticamente.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="CEP" value={f.postalCode} onChange={e => patch({ postalCode: maskCep(e.target.value) })} placeholder="00000-000"
              actionRight={<button type="button" onClick={lookupCep} disabled={cepLoading} aria-label="Buscar CEP"
                className="cursor-pointer inline-flex items-center justify-center w-7 h-7 rounded-md text-gray-500 hover:text-brand-600 hover:bg-gray-100 disabled:opacity-50"><Search size={14} /></button>} />
            <div className="md:col-span-2"><Input label="Logradouro" value={f.address} onChange={e => patch({ address: e.target.value })} placeholder="Rua, avenida" /></div>
            <Input label="Número" value={f.number} onChange={e => patch({ number: e.target.value })} placeholder="123" />
            <Input label="Complemento" value={f.complement} onChange={e => patch({ complement: e.target.value })} placeholder="Sala, bloco" />
            <Input label="Bairro" value={f.district} onChange={e => patch({ district: e.target.value })} placeholder="Bairro" />
            <Select label="Estado" value={f.stateId} onChange={e => patch({ stateId: e.target.value, cityId: '' })} placeholder="UF" options={states.map(s => ({ value: s.id, label: s.abbreviation }))} />
            <div className="md:col-span-2"><Select label="Cidade" value={f.cityId} onChange={e => patch({ cityId: e.target.value })} placeholder={f.stateId ? 'Selecione' : 'Escolha o estado primeiro'} disabled={!f.stateId} options={cities.map(c => ({ value: c.id, label: c.city }))} /></div>
          </div>
        </div>
      ),
    },
    {
      key: 'bancarios', label: 'Dados bancários', icon: Landmark,
      content: (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-1">Dados bancários</h2>
          <p className="text-xs text-gray-500 mb-4">Como o usuário receberá os repasses.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Select label="Tipo de transação" value={f.transaction} onChange={e => patch({ transaction: e.target.value })}
              placeholder="Selecione" options={TRANSACTION_TYPES} searchable={false} />
            <Input label="Beneficiário" wrapperClassName="md:col-span-2" value={f.beneficiary}
              onChange={e => patch({ beneficiary: e.target.value })} placeholder="Nome do titular da conta" />

            {f.transaction === 'PIX' && (
              <Input label="Chave PIX" wrapperClassName="md:col-span-3" value={f.pixKey}
                onChange={e => patch({ pixKey: e.target.value })} placeholder="CPF, e-mail, telefone ou chave aleatória" />
            )}

            {(f.transaction === 'DEPÓSITO' || f.transaction === 'TRANSFERÊNCIA') && (
              <>
                <Select label="Banco" value={f.bankId} onChange={e => patch({ bankId: e.target.value })}
                  placeholder="Selecione o banco" options={banks.map(b => ({ value: b.id, label: `${b.code} — ${b.name}` }))} />
                <Input label="Agência" value={f.agency} onChange={e => patch({ agency: e.target.value })} placeholder="0001" />
                <Input label="Conta" value={f.account} onChange={e => patch({ account: e.target.value })} placeholder="12345-6" />
                <Select label="Tipo de conta" value={f.accountType} onChange={e => patch({ accountType: e.target.value })}
                  placeholder="Selecione" options={ACCOUNT_TYPES} searchable={false} />
              </>
            )}

            {f.transaction && (
              <>
                <Select label="Titular (PF/PJ)" value={f.accountHolderType}
                  onChange={e => patch({ accountHolderType: e.target.value, bankDocument: maskDoc(e.target.value, f.bankDocument) })}
                  options={PERSON_OPTS} searchable={false} clearable={false} />
                <Input label={f.accountHolderType === 'PESSOA FÍSICA' ? 'CPF do titular' : 'CNPJ do titular'}
                  wrapperClassName="md:col-span-2" value={f.bankDocument}
                  onChange={e => patch({ bankDocument: maskDoc(f.accountHolderType || 'PESSOA JURÍDICA', e.target.value) })}
                  placeholder={f.accountHolderType === 'PESSOA FÍSICA' ? '000.000.000-00' : '00.000.000/0000-00'} />
              </>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'comissoes', label: 'Comissões', icon: Percent, hasError: comissaoErr,
      content: (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Comissões</h2>
          <div className="space-y-4">
            <div>
              <label className="text-xs font-medium text-gray-700 mb-1.5 block">Modelo de comissão</label>
              <div className="grid grid-cols-3 rounded-md ring-1 ring-gray-200 overflow-hidden max-w-md">
                {([['NONE', 'Sem comissão'], ['NETWORK', 'Rede'], ['REGRESSIVE', 'Regressiva']] as const).map(([val, label], i) => {
                  const active = f.commissionMode === val
                  return (
                    <button key={val} type="button" onClick={() => { patch({ commissionMode: val }); clearError('networkPct'); clearError('commissionId') }}
                      className={`cursor-pointer w-full px-4 py-2 text-[13px] font-medium text-center transition ${i > 0 ? 'border-l border-gray-200' : ''} ${active ? 'bg-brand-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>{label}</button>
                  )
                })}
              </div>
            </div>
            {f.commissionMode === 'NETWORK' && (
              <div className="max-w-xs"><PercentInput label="Percentual de rede (0 a 100)" value={f.networkPct === '' ? null : Number(f.networkPct)} onChange={v => { patch({ networkPct: v === null ? '' : String(v) }); clearError('networkPct') }} placeholder="Ex.: 5" error={errors.networkPct} /></div>
            )}
            {f.commissionMode === 'REGRESSIVE' && (
              <div className="max-w-md"><Select label="Estrutura de comissão (comercial)" required value={f.commissionId} onChange={e => { patch({ commissionId: e.target.value }); clearError('commissionId') }} placeholder="Selecione a estrutura" options={commercialCommissions.map(c => ({ value: c.id, label: c.name }))} error={errors.commissionId} /></div>
            )}
            {f.commissionMode === 'NONE' && <p className="text-sm text-gray-500">Este usuário não receberá comissão.</p>}
          </div>
        </div>
      ),
    },
  ]

  if (isAffiliate) {
    sections.push({
      key: 'admfat', label: 'Administrativo e faturamento', icon: FileText,
      content: (
        <div className="bg-white rounded-md ring-1 ring-gray-200 p-5 sm:p-6">
          <h2 className="text-sm font-semibold text-gray-800 mb-4">Responsáveis</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input label="Nome do responsável administrativo" value={f.admName} onChange={e => patch({ admName: e.target.value })} placeholder="Nome completo" />
            <Input label="E-mail do responsável administrativo" type="email" value={f.admEmail} onChange={e => patch({ admEmail: e.target.value })} placeholder="adm@empresa.com.br" />
            <Input label="Nome do responsável pelo faturamento" value={f.billName} onChange={e => patch({ billName: e.target.value })} placeholder="Nome completo" />
            <Input label="E-mail do responsável pelo faturamento" type="email" value={f.billEmail} onChange={e => patch({ billEmail: e.target.value })} placeholder="financeiro@empresa.com.br" />
          </div>
        </div>
      ),
    })
  }

  sections.push({
    key: 'documentos', label: 'Documentos', icon: Paperclip,
    content: (
      <DocumentsManager
        service={standardDocuments(`/administractive/users/${id}`)}
        enabled={editing}
        readOnly={!canEdit}
        description="Anexe e gerencie os documentos do usuário."
      />
    ),
  })

  return (
    <>
      {header}
      <div className="flex items-center justify-between mb-3">
        {meta && <Badge variant="blue">{meta.label}</Badge>}
        {!editing && <Button size="xs" variant="ghost" iconLeft={<ArrowLeft size={12} />} onClick={() => setStep(1)}>Trocar tipo</Button>}
      </div>
      {loading ? <div className="py-10 text-center text-sm text-gray-400">Carregando</div>
        : <FormSidemenu sections={sections} activeKey={activeSection} onChange={setActiveSection} />}
    </>
  )
}
